import { useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { InfoHint, Skeleton } from '@solid/ui';
import { AlertTriangle, BarChart3, CheckCircle2, ClipboardList, MessageSquare } from 'lucide-react';
import { formsApi, projectsApi, type MyFormsResult, type ProjectStats } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { useOpenForm } from '@/components/forms/FormListRow';
import { cn } from '@/lib/utils';

const SECTION_LABEL = 'text-caption font-semibold uppercase tracking-wider text-grey-500';

/** One place decides how a wait is written: minutes, then hours, then days. */
function formatDuration(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`;
  const round1 = (x: number) => Math.round(x * 10) / 10;
  const hours = round1(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = round1(minutes / 60 / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** weekStart is a calendar date (YYYY-MM-DD), not an instant — read it as one. */
function weekDate(weekStart: string): Date {
  const [y, m, d] = weekStart.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * The overview's "At a glance" card: what's waiting on the viewer's side, the
 * team's typical response time, and weekly project activity.
 *
 * ⚠ Nothing here puts the client in a bad light (Jesse, 2026-10-06): no
 * team-vs-client comparisons, activity is everyone combined, and waiting items
 * carry no overdue colouring. Hours stay in their own card and nowhere else.
 */
export function AtAGlanceCard({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['project-stats', projectId],
    queryFn: () => projectsApi.stats(projectId),
    staleTime: 60_000,
  });

  // Forms waiting on a client come from the same query as the Forms page and
  // the Dashboard, so the three never disagree about what's outstanding.
  const { data: myForms } = useQuery({
    queryKey: ['myForms'],
    queryFn: () => formsApi.my(),
    staleTime: 30_000,
    enabled: data?.perspective === 'client',
  });
  const { open: openForm, startingId } = useOpenForm();

  if (isLoading) return <Skeleton className="h-56 rounded-xl" />;

  const header = (
    <div className="mb-4 flex items-center gap-2">
      <BarChart3 className="h-5 w-5 text-accent" />
      <h2 className="text-lg font-semibold text-secondary">At a glance</h2>
    </div>
  );

  if (isError || !data) {
    return (
      <Card>
        {header}
        <p className="flex items-center gap-1.5 text-sm font-semibold text-error">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          Couldn't load
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-1.5 text-sm font-semibold text-accent-700 underline-offset-2 hover:underline"
        >
          Retry
        </button>
      </Card>
    );
  }

  const forms =
    data.perspective === 'client'
      ? (myForms?.assigned ?? []).filter((r) => r.form.projectId === projectId)
      : [];

  // Lives in the main column (Jesse, 2026-10-06): what's waiting beside the
  // response time, the activity chart full width beneath. Two rows rather
  // than three columns because the column is only ~630px on a 1280px laptop.
  return (
    <Card data-testid="at-a-glance">
      {header}
      <div className={cn('grid gap-4', data.responseTime && 'sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-8')}>
        <Waiting
          stats={data}
          projectId={projectId}
          forms={forms}
          onOpenForm={openForm}
          startingFormId={startingId}
        />
        {data.responseTime && (
          <ResponseTime
            medianMs={data.responseTime.medianMs}
            samples={data.responseTime.samples}
            windowDays={data.windowDays}
            perspective={data.perspective}
          />
        )}
      </div>
      <Activity weeks={data.activity.weeks} />
    </Card>
  );
}

type WaitingForm = MyFormsResult['assigned'][number];

function Waiting({
  stats,
  projectId,
  forms,
  onOpenForm,
  startingFormId,
}: {
  stats: ProjectStats;
  projectId: string;
  forms: WaitingForm[];
  onOpenForm: (formId: string, existing: WaitingForm) => void;
  startingFormId: string | null;
}) {
  const label = stats.perspective === 'team' ? 'Waiting on us' : 'Waiting on you';
  const total = stats.waiting.total + forms.length;
  const unlisted = stats.waiting.total - stats.waiting.discussions.length;

  return (
    <section>
      <div className="flex items-center justify-between">
        <h3 className={SECTION_LABEL}>{label}</h3>
        {total > 0 && <span className="text-sm tabular-nums text-grey-500">{total}</span>}
      </div>

      {total === 0 ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-grey-500">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
          Nothing {label.toLowerCase()}
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {stats.waiting.discussions.map((d) => (
            <li key={d.id}>
              <Link
                to={
                  d.discussionType
                    ? `/projects/${projectId}/custom/${d.discussionType}/${d.id}`
                    : `/projects/${projectId}/discussions/${d.id}`
                }
                className="flex items-center gap-2 rounded py-1 text-sm text-secondary transition-colors hover:text-accent"
              >
                <MessageSquare className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
                <span className="truncate">{d.title || 'Untitled discussion'}</span>
              </Link>
            </li>
          ))}
          {forms.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onOpenForm(r.formId, r)}
                disabled={startingFormId === r.formId}
                className="flex w-full items-center gap-2 rounded py-1 text-left text-sm text-secondary transition-colors hover:text-accent disabled:opacity-60"
              >
                <ClipboardList className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
                <span className="truncate">{r.form.title}</span>
              </button>
            </li>
          ))}
          {unlisted > 0 && (
            <li>
              <Link
                to={`/projects/${projectId}/discussions`}
                className="block py-1 text-sm text-accent hover:text-accent/80"
              >
                +{unlisted} more
              </Link>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function ResponseTime({
  medianMs,
  samples,
  windowDays,
  perspective,
}: {
  medianMs: number;
  samples: number;
  windowDays: number;
  perspective: ProjectStats['perspective'];
}) {
  return (
    <section className="border-t border-grey-100 pt-4 sm:min-w-[200px] sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0">
      <h3 className={cn(SECTION_LABEL, 'flex items-center gap-1')}>
        <span className="whitespace-nowrap">
          Typical response time
          <InfoHint label="Typical response time" className="ml-1 align-middle">
            <p>
              {perspective === 'team'
                ? 'How long we usually take to reply after a client posts in a discussion.'
                : 'How long the team usually takes to reply after you post in a discussion.'}{' '}
              It's the median over the last {windowDays} days, from {samples} replies.
            </p>
            <p className="mt-2">
              Weekends don't count, so a Friday-evening message answered Monday morning counts as a
              few hours. A quick thank-you doesn't start the clock, and an update more than two
              weeks later counts as a new conversation, not a slow reply.
            </p>
            {perspective === 'team' && <p className="mt-2">Clients see this figure too.</p>}
          </InfoHint>
        </span>
      </h3>
      <p className="mt-1 text-heading-2 text-secondary">{formatDuration(medianMs)}</p>
      <p className="text-sm text-grey-500">Last {windowDays} days</p>
    </section>
  );
}

function Activity({ weeks }: { weeks: ProjectStats['activity']['weeks'] }) {
  const [active, setActive] = useState<number | null>(null);
  const total = weeks.reduce((sum, w) => sum + w.count, 0);
  const max = Math.max(0, ...weeks.map((w) => w.count));
  // Label the extreme, nothing else: the latest of the busiest weeks.
  const peak = max > 0 ? weeks.map((w) => w.count).lastIndexOf(max) : -1;

  const fmtWeek = (i: number) =>
    weekDate(weeks[i].weekStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  const onKeyDown = (e: KeyboardEvent) => {
    const last = weeks.length - 1;
    const at = active ?? last;
    const next =
      e.key === 'ArrowLeft' ? Math.max(0, at - 1)
      : e.key === 'ArrowRight' ? Math.min(last, at + 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  };

  return (
    <section className="mt-4 border-t border-grey-100 pt-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className={SECTION_LABEL}>Project activity</h3>
        {total > 0 && (
          <span className="text-sm text-grey-500" aria-live="polite">
            {active !== null ? (
              <>
                <span className="font-semibold text-secondary">{weeks[active].count}</span> · week of{' '}
                {fmtWeek(active)}
              </>
            ) : (
              <>
                <span className="font-semibold text-secondary">{total}</span> in {weeks.length} weeks
              </>
            )}
          </span>
        )}
      </div>
      <p className="text-sm text-grey-500">Posts and replies per week</p>

      {total === 0 ? (
        <p className="mt-3 text-sm text-grey-500">No posts or replies in the last {weeks.length} weeks</p>
      ) : (
        <>
          <div
            role="group"
            aria-label={`Posts and replies per week, last ${weeks.length} weeks. Use the arrow keys to read each week.`}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onFocus={() => setActive((a) => a ?? weeks.length - 1)}
            onBlur={() => setActive(null)}
            onPointerLeave={() => setActive(null)}
            className="mt-3 flex h-20 items-end gap-0.5 rounded border-b border-grey-200 pt-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
          >
            {weeks.map((w, i) => (
              <div
                key={w.weekStart}
                onPointerEnter={() => setActive(i)}
                className="relative flex h-full flex-1 items-end justify-center"
              >
                {w.count > 0 && (
                  <div
                    className={cn(
                      'relative w-full max-w-[24px] rounded-t transition-colors',
                      active === i ? 'bg-accent-700' : 'bg-accent',
                    )}
                    style={{ height: `${Math.max(4, (w.count / max) * 100)}%` }}
                  >
                    {i === peak && (
                      <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] tabular-nums text-grey-500">
                        {w.count}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Month ticks under the first week of each month */}
          <div className="mt-1 flex gap-0.5" aria-hidden>
            {weeks.map((w, i) => {
              const month = weekDate(w.weekStart).getMonth();
              const showMonth = i === 0 || month !== weekDate(weeks[i - 1].weekStart).getMonth();
              return (
                <div key={w.weekStart} className="flex-1 overflow-visible whitespace-nowrap text-[10px] text-grey-400">
                  {showMonth ? weekDate(w.weekStart).toLocaleDateString(undefined, { month: 'short' }) : ''}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
